/*
 * Governance Tile · Phase 4 · "How the Judge is going" column.
 *
 * Renders the Judge column of the three-column live scorecard.  Every
 * measured metric binds to the live response from
 *   GET /api/admin/governance-tile/summary
 * exposed via useTileData().  Every metric the endpoint does not yet
 * compute renders as an em dash with a "not yet measured" tooltip and
 * a `-pending` testid marker, so no mockup placeholder integer ever
 * ships (\u00A7 V.6 attestation-honesty rule \u00B7 anchor GB-013).
 *
 * Per-parent sub-parameter scores (C1\u2013C6 and Q1\u2013Q5) and Judge
 * session-start ritual sub-checks are all "seed estimates" in the
 * mockup pending IJ-045 / IJ-034 backend wiring.  This renderer
 * replaces every estimate with an em dash and clamps every parent-row
 * chip and every sub-check glyph to the pending marker.
 *
 * IJ-084 \u00B7 2026-07-24 \u00B7 Phase 4 of the v4-to-v8.8.12 re-cut.
 */
import { useState } from 'react';
import Row from './Row';
import {
  JUDGE_SCORECARD_TITLE,
  JUDGE_SCORECARD_ICON,
  JUDGE_SUBTITLE,
  JUDGE_HEADLINE_SUB_REST_PREFIX,
  JUDGE_HEADLINE_SUB_REST_TAIL,
  JUDGE_HEADLINE_SUB_ZERO,
  JUDGE_STATUS_ZERO,
  JUDGE_STATUS_UNDERPERFORMING,
  JUDGE_STATUS_ON_TRACK,
  JUDGE_SUBTOPIC_SECTION_LABEL,
  JUDGE_SUBTOPIC_SECTION_TOOLTIP,
  JUDGE_PARENT_GROUPS,
  JUDGE_BLOCKING_GATE_LABEL,
  JUDGE_BLOCKING_GATE_TOOLTIP,
  JUDGE_FALSE_NEG_LABEL,
  JUDGE_FALSE_NEG_TOOLTIP,
  JUDGE_INVOCATION_SECTION_LABEL,
  JUDGE_INVOCATION_ROWS,
} from '../copy/judgeColumn';

const PENDING = '\u2014'; // em dash
const PARENT_PENDING_CHIP = 'Not measured yet';
const PENDING_TOOLTIP =
  'Not measured yet. Live counter pending IJ-034 / IJ-045 (per-sub-parameter Judge scoring service).';

// Judge invocation discipline row-key -> extractor(summary) or null.
// null == the endpoint does not emit this signal yet (pending IJ-034).
const INVOCATION_MAP = {
  // Endpoint field `judge_missing_reasoning_count` counts entries with
  // a PASS verdict but empty JUDGE REASONING.  IJ-027 is grandfathered
  // via a `GRANDFATHERED:` marker so it does NOT count.
  never_ran: (s) => ({
    value: String(s.judge_missing_reasoning_count ?? 0),
    tone: (s.judge_missing_reasoning_count ?? 0) > 0 ? 'red' : 'green',
  }),
  // Manual override scan not yet emitted.  Pending IJ-034.
  manual_overrides: null,
  // Static governance fact \u2014 not a mock integer.  Ban was armed on
  // 2026-07-18 17:55 UTC under SPD v5.91 \u00B7 IJ-028.  We surface the
  // ISO date only; the exact minute is codified in the ledger.
  ban_armed_since: () => ({ value: '2026-07-18', tone: 'slate' }),
  // Endpoint field `avg_check_time_seconds` returns a float or null.
  avg_check_time: (s) => {
    const v = s.avg_check_time_seconds;
    if (v === null || v === undefined) {
      return { value: PENDING, tone: 'slate' };
    }
    return { value: `${v.toFixed(1)} s`, tone: 'slate' };
  },
};

function SectionHeader({ label, tooltip }) {
  return (
    <div className="flex items-center gap-1 mt-4 mb-2">
      <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
        {label}
      </span>
      {tooltip ? (
        <span
          title={tooltip}
          className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-600 text-[9px] text-slate-400 cursor-help"
        >
          ?
        </span>
      ) : null}
    </div>
  );
}

// SPD v6.01 · IJ-092 — sub-check key mappings for the Judge coding
// and QA parent groups.  Judge SS ritual (judgess) stays pending
// because no backend signal tracks Judge session-start attestation
// yet.
const LIVE_PARENT_SUB_KEYS = {
  coding: ['c1', 'c2', 'c3', 'c4', 'c5', 'c6'],
  qa: ['q1', 'q2', 'q3', 'q4', 'q5'],
};

function parentSubState(groupKey, i, summary) {
  const keys = LIVE_PARENT_SUB_KEYS[groupKey];
  if (!keys || !summary?.judge_sub_check_counts) return { measured: false };
  const endpointKey = keys[i];
  if (!endpointKey) return { measured: false };
  const hits = summary.judge_sub_check_counts[endpointKey] ?? 0;
  return { measured: true, hits };
}

function parentChip(group, summary) {
  const keys = LIVE_PARENT_SUB_KEYS[group.key];
  if (!keys || !summary?.judge_sub_check_counts) return PARENT_PENDING_CHIP;
  const passed = keys.filter((k) => (summary.judge_sub_check_counts[k] ?? 0) > 0).length;
  return `${passed}/${keys.length} judged`;
}

function ParentGroup({ group, summary }) {
  const [open, setOpen] = useState(false);
  const chipText = parentChip(group, summary);
  const isMeasured = chipText !== PARENT_PENDING_CHIP;
  return (
    <div
      data-testid={`governance-tile-judge-parent-${group.key}`}
      className={
        'border-l-4 pl-3 py-1 rounded-r bg-slate-900/40 ' + group.borderColour
      }
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full grid grid-cols-[1fr_auto] gap-x-3 items-center text-left"
      >
        <span className="text-[12px] text-slate-200 leading-snug">
          <span
            title={group.tooltip}
            className="mr-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-600 text-[9px] text-slate-400 cursor-help align-middle"
          >
            ?
          </span>
          {group.parent}
        </span>
        <span
          data-testid={`governance-tile-judge-parent-${group.key}-chip`}
          className={
            'text-[10px] font-medium tabular-nums whitespace-nowrap px-2 py-0.5 rounded border ' +
            (isMeasured
              ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
              : 'border-slate-500/30 bg-slate-500/10 text-slate-300')
          }
        >
          {chipText}
        </span>
      </button>
      {open ? (
        <ul className="mt-2 space-y-0.5 pl-1">
          {group.subChecks.map((sc, i) => {
            const state = parentSubState(group.key, i, summary);
            let glyph = '\u25EF';
            let glyphColour = 'text-slate-500';
            let badgeText = PENDING;
            let badgeTone = 'text-slate-300 bg-slate-500/10 border-slate-500/30';
            let testIdSuffix = '-pending';
            if (state.measured) {
              testIdSuffix = '';
              badgeText = String(state.hits);
              if (state.hits > 0) {
                glyph = '\u2713';
                glyphColour = 'text-emerald-400';
                badgeTone = 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30';
              } else {
                glyph = '\u2717';
                glyphColour = 'text-rose-400';
                badgeTone = 'text-rose-300 bg-rose-500/10 border-rose-500/30';
              }
            }
            return (
              <li
                key={i}
                data-testid={`governance-tile-judge-parent-${group.key}-sub-${i}${testIdSuffix}`}
                className="grid grid-cols-[1fr_auto] gap-x-3 items-baseline text-[11px] text-slate-400 leading-snug py-0.5"
              >
                <span>
                  <span className={`mr-1.5 ${glyphColour}`}>{glyph}</span>
                  {sc.label}
                </span>
                <span
                  className={
                    'px-2 py-0.5 rounded border text-[10px] font-medium tabular-nums whitespace-nowrap ' +
                    badgeTone
                  }
                >
                  {badgeText}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

// SPD v6.03 · IJ-093 — Retroactive misses section.
const RETRO_SECTION_LABEL = 'Retroactive misses logged this session';
const RETRO_SECTION_TOOLTIP =
  'Judge misses and reply-attribution errors recorded to /app/memory/JUDGE_LESSONS.md or /app/memory/GOVERNANCE_LEDGER.md today (UTC calendar day). Each row is a real incident the Judge did not intercept at ship-time.';

function RetroMissesSection({ summary }) {
  const misses = summary?.judge_retro_misses_today;
  const replyErrs = summary?.session_reply_errors_today;
  const measured = !!misses && !!replyErrs;
  const items = measured
    ? [
        ...(misses.items || []).map((it) => ({ ...it, kind: 'lesson' })),
        ...(replyErrs.items || []).map((it) => ({ ...it, kind: 'gb' })),
      ]
    : [];
  const totalCount = measured ? (misses.count || 0) + (replyErrs.count || 0) : null;

  return (
    <div
      data-testid={
        measured
          ? 'governance-tile-judge-retro-section'
          : 'governance-tile-judge-retro-section-pending'
      }
      className="mt-3 rounded p-2 border border-dashed border-slate-700/70 bg-slate-900/30"
    >
      <div className="grid grid-cols-[1fr_auto] gap-x-3 items-baseline mb-1">
        <span className="text-[11px] font-semibold text-slate-200 leading-snug">
          {RETRO_SECTION_LABEL}
          <span
            title={RETRO_SECTION_TOOLTIP}
            className="ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-600 text-[9px] text-slate-400 cursor-help"
          >
            ?
          </span>
        </span>
        <span
          data-testid="governance-tile-judge-retro-chip"
          className={
            'px-2 py-0.5 rounded border text-[10px] font-medium tabular-nums whitespace-nowrap ' +
            (!measured
              ? 'border-slate-500/30 bg-slate-500/10 text-slate-300'
              : totalCount === 0
                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                : 'border-rose-500/40 bg-rose-500/10 text-rose-300')
          }
        >
          {!measured ? PENDING : totalCount === 0 ? 'None today' : `${totalCount} today`}
        </span>
      </div>
      {measured && totalCount > 0 ? (
        <ul className="mt-1 space-y-0.5 pl-1">
          {items.map((it) => (
            <li
              key={it.id}
              data-testid={`governance-tile-judge-retro-item-${it.id}`}
              className="grid grid-cols-[auto_auto_1fr] gap-x-2 items-baseline text-[11px] leading-snug py-0.5"
            >
              <span className="text-rose-400">{'\u2717'}</span>
              <span className="font-mono text-[10px] text-slate-300 tabular-nums">{it.id}</span>
              <span className="text-slate-400 truncate" title={it.incident}>
                {it.incident || '\u2014'}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function InvocationRow({ row, summary }) {
  const extractor = INVOCATION_MAP[row.key];
  if (extractor === null || extractor === undefined) {
    return (
      <Row
        label={row.label}
        tooltip={
          row.tooltip ? `${row.tooltip} \u00B7 ${PENDING_TOOLTIP}` : PENDING_TOOLTIP
        }
        value={PENDING}
        tone="slate"
        testId={`governance-tile-judge-invocation-${row.key}-pending`}
      />
    );
  }
  if (!summary) {
    return (
      <Row
        label={row.label}
        tooltip={row.tooltip}
        value={PENDING}
        tone="slate"
        testId={`governance-tile-judge-invocation-${row.key}-loading`}
      />
    );
  }
  const r = extractor(summary);
  return (
    <Row
      label={row.label}
      tooltip={row.tooltip}
      value={r.value}
      tone={r.tone}
      testId={`governance-tile-judge-invocation-${row.key}`}
    />
  );
}

export default function JudgeColumn({ summary, loading, error }) {
  const workingSummary = error ? null : summary;
  const cleanJudgements = workingSummary?.judge_proactive_catches ?? null;
  const tasksTotal = workingSummary?.tasks_total ?? null;
  const judgeFalseNegatives = workingSummary?.judge_false_negatives ?? null;

  // Live headline value + suffix.  When summary is still loading both
  // halves render em dash.  When tasksTotal is 0 the denominator still
  // renders as ' of 0' — factually true from the endpoint, not the
  // stale '48' the mockup was drafted against (operator directive
  // 2026-07-24 · 48 was last session's number).
  const headlineValue = cleanJudgements === null ? PENDING : String(cleanJudgements);
  const headlineSuffix =
    tasksTotal === null ? ` ${PENDING}` : ` of ${tasksTotal}`;

  // Live status chip.
  let statusChip;
  let statusChipTone;
  if (tasksTotal === null || cleanJudgements === null) {
    statusChip = PENDING;
    statusChipTone = 'slate';
  } else if (tasksTotal === 0) {
    statusChip = JUDGE_STATUS_ZERO;
    statusChipTone = 'slate';
  } else if (cleanJudgements < tasksTotal) {
    statusChip = `${JUDGE_STATUS_UNDERPERFORMING} \u00B7 ${cleanJudgements} of ${tasksTotal}`;
    statusChipTone = 'red';
  } else {
    statusChip = `${JUDGE_STATUS_ON_TRACK} \u00B7 ${cleanJudgements} of ${tasksTotal}`;
    statusChipTone = 'green';
  }

  // Live sub-line: red half + rest half.
  let subRedText;
  let subRestText;
  if (tasksTotal === null || cleanJudgements === null) {
    subRedText = PENDING;
    subRestText = '';
  } else if (tasksTotal === 0) {
    subRedText = JUDGE_HEADLINE_SUB_ZERO;
    subRestText = '';
  } else {
    const notClean = tasksTotal - cleanJudgements;
    subRedText = `${notClean} of ${tasksTotal} tasks did NOT get a clean Judge Judgement`;
    subRestText = `${JUDGE_HEADLINE_SUB_REST_PREFIX} \u00B7 today Judge produced a clean Judgement on ${cleanJudgements} of ${tasksTotal}${JUDGE_HEADLINE_SUB_REST_TAIL}`;
  }

  // Judge false-negative alert row: render-gated behind > 0.
  const showFalseNegRow = (judgeFalseNegatives ?? 0) > 0;

  return (
    <section
      data-testid="governance-tile-judge-column"
      className="rounded-xl border border-slate-800/70 bg-slate-950/40 p-5"
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <span className="text-[16px] text-slate-300" aria-hidden="true">
            {JUDGE_SCORECARD_ICON}
          </span>
          <h3 className="text-[15px] font-semibold text-slate-100">
            {JUDGE_SCORECARD_TITLE}
          </h3>
        </div>
        <span
          data-testid="governance-tile-judge-status-chip"
          className={
            'text-[10px] font-medium px-2 py-0.5 rounded border whitespace-nowrap ' +
            (statusChipTone === 'red'
              ? 'border-rose-500/40 bg-rose-500/10 text-rose-300'
              : statusChipTone === 'green'
                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                : 'border-slate-500/30 bg-slate-500/10 text-slate-300')
          }
        >
          {statusChip}
        </span>
      </div>
      <p className="text-[11px] text-slate-400 leading-snug mb-4">
        {JUDGE_SUBTITLE}
      </p>

      <div className="mb-4">
        <div className="flex items-baseline gap-1">
          <span
            data-testid="governance-tile-judge-headline-value"
            className="text-[36px] font-bold text-slate-100 tabular-nums leading-none"
          >
            {headlineValue}
          </span>
          <span className="text-[13px] text-slate-400">{headlineSuffix}</span>
        </div>
        <p
          data-testid="governance-tile-judge-headline-sub"
          className="text-[11px] leading-snug mt-1"
        >
          <span className="text-rose-300">{subRedText}</span>
          <span className="text-slate-400">{subRestText}</span>
        </p>
      </div>

      <SectionHeader
        label={JUDGE_SUBTOPIC_SECTION_LABEL}
        tooltip={JUDGE_SUBTOPIC_SECTION_TOOLTIP}
      />
      <div className="space-y-1.5 mb-3">
        {JUDGE_PARENT_GROUPS.map((g) => (
          <ParentGroup key={g.key} group={g} summary={workingSummary} />
        ))}
      </div>

      <div
        data-testid={
          workingSummary?.judge_sub_check_counts
            ? 'governance-tile-judge-blocking-gate'
            : 'governance-tile-judge-blocking-gate-pending'
        }
        className="grid grid-cols-[1fr_auto] gap-x-3 items-baseline border-t border-dashed border-slate-700 mt-3 pt-3 pb-1"
      >
        <span className="text-[12px] font-semibold text-slate-100 leading-snug">
          {JUDGE_BLOCKING_GATE_LABEL}
          <span
            title={JUDGE_BLOCKING_GATE_TOOLTIP}
            className="ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-600 text-[9px] text-slate-400 cursor-help"
          >
            ?
          </span>
        </span>
        {(() => {
          const jsc = workingSummary?.judge_sub_check_counts;
          const tasks = workingSummary?.tasks_total ?? null;
          if (!jsc || tasks === null) {
            return (
              <span
                className="px-2 py-0.5 rounded border text-[11px] font-medium tabular-nums whitespace-nowrap text-slate-300 bg-slate-500/10 border-slate-500/30"
                title={PENDING_TOOLTIP}
              >
                {PENDING}
              </span>
            );
          }
          const pje = jsc.parents_judged_e2e ?? 0;
          const tone =
            tasks === 0
              ? 'text-slate-300 bg-slate-500/10 border-slate-500/30'
              : pje === tasks
                ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30'
                : 'text-rose-300 bg-rose-500/10 border-rose-500/30';
          return (
            <span
              className={
                'px-2 py-0.5 rounded border text-[11px] font-medium tabular-nums whitespace-nowrap ' +
                tone
              }
            >
              {`${pje} of ${tasks}`}
            </span>
          );
        })()}
      </div>

      {/* SPD v6.03 · IJ-093 — Retroactive misses logged this session.
          Surfaces LESSON-004 · GB-025 · GB-026 (and any future same-
          class misses) on the tile so a future session cannot miss
          them at a glance. Rose glyphs when items exist; emerald
          "None today" chip when both counts are zero and the endpoint
          has responded. */}
      <RetroMissesSection summary={workingSummary} />

      {showFalseNegRow ? (
        <div
          data-testid="governance-tile-judge-false-negative"
          className="mt-3 rounded p-2 bg-rose-500/10 border-l-2 border-rose-500 grid grid-cols-[1fr_auto] gap-x-3 items-baseline"
        >
          <span className="text-[11px] text-rose-300 leading-snug">
            {JUDGE_FALSE_NEG_LABEL}
            <span
              title={JUDGE_FALSE_NEG_TOOLTIP}
              className="ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full border border-rose-500/40 text-[9px] text-rose-300 cursor-help"
            >
              ?
            </span>
          </span>
          <span className="px-2 py-0.5 rounded border text-[11px] font-medium tabular-nums whitespace-nowrap text-rose-300 bg-rose-500/10 border-rose-500/30">
            +{judgeFalseNegatives}
          </span>
        </div>
      ) : null}

      <SectionHeader label={JUDGE_INVOCATION_SECTION_LABEL} />
      <div className="space-y-0">
        {JUDGE_INVOCATION_ROWS.map((r) => (
          <InvocationRow key={r.key} row={r} summary={workingSummary} />
        ))}
      </div>

      {loading ? (
        <p
          data-testid="governance-tile-judge-loading"
          className="mt-3 text-[10px] italic text-slate-500"
        >
          Live counters loading&hellip;
        </p>
      ) : null}
      {error ? (
        <p
          data-testid="governance-tile-judge-error"
          className="mt-3 text-[10px] text-rose-400"
        >
          Live counter fetch failed. Rows show pending until the endpoint recovers.
        </p>
      ) : null}
    </section>
  );
}
